import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, Float, Grid, OrbitControls, useTexture } from "@react-three/drei";
import { XR, createXRStore } from "@react-three/xr";
import * as THREE from "three";
import { toast } from "sonner";
import { Volume2, VolumeX } from "lucide-react";
import { createForestAmbient } from "@/lib/ambient";
import { api } from "@/lib/api";

const PANO_IMG = "https://static.prod-images.emergentagent.com/jobs/1c220779-6edd-4bd3-b2c7-23a9d0486762/images/30772e068c67c998a3f9d7e1635210799fdcb80e5fd48302cae8ec811f38af6c.jpeg";

function PanoSphere() {
  const texture = useTexture(PANO_IMG);
  return (
    <mesh scale={[-1, 1, 1]} position={[0, 6, 0]}>
      <sphereGeometry args={[70, 48, 32]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} color="#8fc9a6" />
    </mesh>
  );
}

const xrStore = createXRStore();

function Portal({ position, label, color, onEnter }) {
  const ring = useRef();
  const [hover, setHover] = useState(false);
  useFrame((_, dt) => {
    if (ring.current) ring.current.rotation.z += dt * 0.6;
  });
  return (
    <group position={position}>
      <mesh
        ref={ring}
        onClick={onEnter}
        onPointerOver={() => { setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      >
        <torusGeometry args={[1.6, 0.12, 16, 64]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={hover ? 2.4 : 1.2} />
      </mesh>
      <mesh onClick={onEnter}>
        <circleGeometry args={[1.45, 48]} />
        <meshStandardMaterial color="#0C1F14" emissive={color} emissiveIntensity={hover ? 0.5 : 0.18} transparent opacity={0.85} />
      </mesh>
      <Text position={[0, 2.3, 0]} fontSize={0.42} color={color} anchorX="center" outlineWidth={0.015} outlineColor="#000">
        {label}
      </Text>
      <Text position={[0, -2.15, 0]} fontSize={0.18} color="#8b8b9e" anchorX="center">
        click to enter
      </Text>
      <pointLight color={color} intensity={6} distance={7} position={[0, 0, 1]} />
    </group>
  );
}

function ShopKiosk({ onEnter }) {
  const [hover, setHover] = useState(false);
  return (
    <group position={[-9, 0, -2]} rotation={[0, 0.8, 0]}>
      <mesh
        position={[0, 1.4, 0]}
        onClick={onEnter}
        onPointerOver={() => { setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      >
        <boxGeometry args={[3, 2.8, 2]} />
        <meshStandardMaterial color="#1B4030" emissive="#2EE38A" emissiveIntensity={hover ? 0.7 : 0.3} />
      </mesh>
      <mesh position={[0, 3.2, 0]}>
        <boxGeometry args={[3.6, 0.35, 2.4]} />
        <meshStandardMaterial color="#2EE38A" emissive="#2EE38A" emissiveIntensity={1.4} />
      </mesh>
      <Text position={[0, 1.5, 1.05]} fontSize={0.55} color="#2EE38A" anchorX="center" outlineWidth={0.02} outlineColor="#000">
        SHOP
      </Text>
      <Text position={[0, 0.8, 1.05]} fontSize={0.16} color="#c9c9d8" anchorX="center">
        characters · badges · passes
      </Text>
      <pointLight color="#2EE38A" intensity={8} distance={9} position={[0, 2.5, 2]} />
    </group>
  );
}

function MatchScreen({ matches, onEnter }) {
  const [hover, setHover] = useState(false);
  const lines = matches.slice(0, 3);
  return (
    <group position={[9, 0, -3]} rotation={[0, -0.7, 0]}>
      <mesh position={[0, 2.6, -0.1]}>
        <boxGeometry args={[7.6, 4.6, 0.2]} />
        <meshStandardMaterial color="#101A12" />
      </mesh>
      <mesh
        position={[0, 2.6, 0.02]}
        onClick={onEnter}
        onPointerOver={() => { setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
      >
        <planeGeometry args={[7.2, 4.2]} />
        <meshStandardMaterial color="#123024" emissive="#A8F0CE" emissiveIntensity={hover ? 0.35 : 0.15} />
      </mesh>
      <Text position={[0, 4.2, 0.1]} fontSize={0.38} color="#A8F0CE" anchorX="center" outlineWidth={0.015} outlineColor="#000">
        ● LIVE MATCHES
      </Text>
      {lines.length === 0 && (
        <Text position={[0, 2.6, 0.1]} fontSize={0.24} color="#8b8b9e" anchorX="center">
          waiting for kickoff…
        </Text>
      )}
      {lines.map((m, i) => (
        <group key={m.match_id}>
          <Text position={[0, 3.45 - i * 1.05, 0.1]} fontSize={0.26} color="#ffffff" anchorX="center">
            {`${m.home}  ${m.score_home} - ${m.score_away}  ${m.away}`}
          </Text>
          <Text position={[0, 3.12 - i * 1.05, 0.1]} fontSize={0.16} color="#8FE3A8" anchorX="center">
            {`${m.minute}' · 1:${m.odds.home}  X:${m.odds.draw}  2:${m.odds.away}`}
          </Text>
        </group>
      ))}
      <Text position={[0, 0.65, 0.1]} fontSize={0.18} color="#FFD600" anchorX="center">
        sports district →
      </Text>
      <pointLight color="#A8F0CE" intensity={8} distance={10} position={[0, 3, 2]} />
    </group>
  );
}

function PlazaScene({ matches, nav }) {
  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[5, 10, 5]} intensity={0.4} />
      <PanoSphere />
      <Grid
        position={[0, 0, 0]}
        args={[80, 80]}
        cellSize={1}
        cellColor="#245239"
        sectionSize={5}
        sectionColor="#2EE38A"
        fadeDistance={45}
        infiniteGrid
      />
      <Float speed={2} rotationIntensity={0.3} floatIntensity={0.6}>
        <Text position={[0, 6.4, -8]} fontSize={1.05} color="#ffffff" anchorX="center" outlineWidth={0.03} outlineColor="#2EE38A">
          METAVERSE ECOSYSTEM
        </Text>
      </Float>
      <Portal position={[-7, 2.2, -7.5]} label="BUSINESS" color="#A8F0CE" onEnter={() => nav("/business")} />
      <Portal position={[-2.4, 2.2, -9.5]} label="ENTERTAINMENT" color="#2EE38A" onEnter={() => nav("/entertainment")} />
      <Portal position={[2.4, 2.2, -9.5]} label="SHOPPING" color="#FFA149" onEnter={() => nav("/shopping")} />
      <Portal position={[7, 2.2, -7.5]} label="SPORTS" color="#8FE3A8" onEnter={() => nav("/sports")} />
      <ShopKiosk onEnter={() => nav("/shopping")} />
      <MatchScreen matches={matches} onEnter={() => nav("/sports")} />
      <OrbitControls
        target={[0, 2.6, -0.5]}
        maxPolarAngle={Math.PI / 1.9}
        enableZoom={false}
        enablePan={false}
        rotateSpeed={-0.4}
      />
    </>
  );
}

export default function Plaza() {
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [vrSupported, setVrSupported] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const ambientRef = useRef(null);

  const startSound = () => {
    if (!ambientRef.current) {
      ambientRef.current = createForestAmbient();
      setSoundOn(true);
    }
  };

  const toggleSound = (e) => {
    e?.stopPropagation();
    if (soundOn) {
      ambientRef.current?.stop();
      ambientRef.current = null;
      setSoundOn(false);
    } else {
      startSound();
    }
  };

  const enterVR = () => {
    if (vrSupported) xrStore.enterVR();
    else toast.info("VR headset not detected — open this page in your headset's browser (Quest, Pico) to enter VR.");
  };

  useEffect(() => {
    return () => ambientRef.current?.stop();
  }, []);

  useEffect(() => {
    const load = () => api.get("/matches").then(({ data }) => setMatches(data.live)).catch(() => {});
    load();
    const iv = setInterval(load, 4000);
    if (navigator.xr?.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then(setVrSupported).catch(() => {});
    }
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="relative h-[calc(100vh-4rem)]" data-testid="plaza-page" onPointerDown={startSound}>
      <Canvas camera={{ position: [0, 2.6, 0.1], fov: 72 }}>
        <XR store={xrStore}>
          <PlazaScene matches={matches} nav={nav} />
        </XR>
      </Canvas>

      <div className="absolute top-4 left-4 pointer-events-none hidden sm:block" data-testid="plaza-overlay">
        <div className="label-mono text-brand-cyan">// metaverse_360 · vr_ready</div>
        <div className="font-display font-extrabold text-xl text-white mt-1">360° view — drag to look around · click a district to enter</div>
      </div>

      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={toggleSound}
          className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-widest transition-colors ${soundOn ? "border-brand-magenta text-brand-magenta" : "border-white/25 text-white/60 hover:text-white"}`}
          data-testid="plaza-sound-toggle"
          title={soundOn ? "Mute ambient sound" : "Play forest ambience"}
        >
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} {soundOn ? "SOUND ON" : "SOUND OFF"}
        </button>
        <button
          onClick={enterVR}
          className={`px-4 py-2 font-display font-bold text-xs uppercase tracking-widest border transition-colors ${vrSupported ? "border-brand-cyan text-brand-cyan hover:bg-brand-cyan hover:text-black" : "border-white/20 text-white/40 hover:text-white"}`}
          data-testid="plaza-enter-vr-button"
          title={vrSupported ? "Enter VR with your headset" : "Open this page in your VR headset browser to enable VR"}
        >
          {vrSupported ? "◉ ENTER VR" : "VR headset not detected"}
        </button>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 flex-wrap justify-center">
        {[
          { label: "Business", to: "/business" },
          { label: "Entertainment", to: "/entertainment" },
          { label: "Shopping", to: "/shopping" },
          { label: "Sports", to: "/sports" },
          { label: "Games", to: "/games" },
        ].map((l) => (
          <button key={l.to} onClick={() => nav(l.to)} className="px-3 py-1.5 text-xs font-mono bg-black/60 border border-white/15 text-white/70 hover:text-white hover:border-brand-magenta transition-colors backdrop-blur" data-testid={`plaza-quick-${l.label.toLowerCase()}`}>
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}
